import React, { useEffect, useMemo, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TablePagination,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import TransactionsDetailsDrawer from "../../components/transaction-details-popup";
import { downloadFile } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import {
  useExportRecentTicketsValidations,
  useGetRecentTicketsValidationsList,
  useGetTicketsValidationApprovalEmployees,
} from "src/api/transactions";
import { useGetCitiesList } from "src/api/general";
import { ICity } from "src/types/generic";
import { DatePicker } from "@mui/x-date-pickers";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { IClientResponse } from "src/types/clients";
import { useGetClientsList } from "src/api/clients";

const tableHeadText = [
  "Ticket Number",
  "Location",
  "Date",
  "Check-in Time",
  "Requested Time",
  "Validation Time",
  "Check-out Time",
  "Total Hours",
  "Validated Hours",
  "Validated Amount",
  "Amount after Validation",
  "Approved By",
  "Notes",
];

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

interface IApprovedBy {
  id: string;
  name: string;
}

const TransactionsTable = () => {
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [tempLocation, setTempLocation] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [openValidationDetails, setOpenValidationDetails] = useState(false);
  const [clickedTrans, setClickedTrans] = useState<any>();
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();
  const [approvedBy, setApprovedBy] = useState<IApprovedBy>();
  const [tempApprovedBy, setTempApprovedBy] = useState<IApprovedBy>();
  const [selectedClient, setSelectedClient] = useState<IClientResponse>();
  const [tempSelectedClient, setTempSelectedClient] =
    useState<IClientResponse>();
  const [page, setPage] = useState(0);

  const fromDate = moment(startDate).startOf("day").valueOf();

  const toDate = moment(endDate).endOf("day").valueOf();

  const filters = {
    location: location?.id,
    clientId: selectedClient?.id,
    approvedBy: approvedBy?.id,
    ...(selectedCity && { cityId: selectedCity.id }),
    ...(startDate && { from: `${fromDate}` }),
    ...(endDate && { to: `${toDate}` }),
    ...(searchBy && { search: searchBy }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const {
    data: transactionsData,
    isLoading,
    status,
    error,
  } = useGetRecentTicketsValidationsList({
    ...filters,
    limit: rowsPerPage,
    page: page + 1,
  });
  const { data: clientsData } = useGetClientsList();

  const { refetch: refetchExport, isFetching: isFetchingExport } =
    useExportRecentTicketsValidations(filters);

  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const { data: approvalEmployeesData, isFetching: isFetchingApprovals } =
    useGetTicketsValidationApprovalEmployees({
      locationId: tempLocation?.id ?? undefined,
      clientId: tempSelectedClient?.id ?? undefined,
    });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    if (searchBy !== "") {
      handleSearch("");
    }
  };

  //Filters

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempLocation(newValue);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleChangeApprovedBy = (
    event: React.SyntheticEvent,
    newValue: any,
  ) => {
    setTempApprovedBy(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleApply = () => {
    setLocation(tempLocation);
    setSearchBy(tempSearchBy);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedCity(tempSelectedCity);
    setSelectedClient(tempSelectedClient);
    setApprovedBy(tempApprovedBy);
  };

  const handleResetFilers = () => {
    setPage(0);
    setLocation(undefined);
    setSearchBy("");
    setStartDate(null);
    setEndDate(null);
    setSelectedCity(undefined);
    setSelectedClient(undefined);
    setApprovedBy(undefined);

    setTempLocation(undefined);
    setTempSearchBy("");
    setTempStartDate(null);
    setTempEndDate(null);
    setTempSelectedCity(undefined);
    setTempSelectedClient(undefined);
    setTempApprovedBy(undefined);
  };

  const handleExport = () => {
    refetchExport().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "tickets-validations");
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "Downloaded successfully",
          },
        ]);
      }
    });
  };

  const handleCloseDrawer = () => {
    setOpenValidationDetails(false);
    setClickedTrans(undefined);
  };

  const handleChangeClient = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedClient(newValue);
  };

  const emptyDataArr = transactionsData?.data?.length === 0;

  useEffect(() => {
    setPage(0);
  }, [location, startDate, endDate, searchBy]);

  useEffect(() => {
    if (transactionsData) {
      setTotalCount(transactionsData?.total);
    }
  }, [transactionsData]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const sortedClients = useMemo(() => {
    return (
      clientsData?.data
        .slice()
        .sort((a, b) =>
          a.clientInfo.nameEn?.localeCompare(b.clientInfo.nameEn),
        ) || []
    );
  }, [clientsData]);

  const filteredLocations = useMemo(() => {
    return tempSelectedClient
      ? sortedLocations.filter(
          (loc: any) => loc.clientId === tempSelectedClient?.id,
        )
      : sortedLocations;
  }, [tempSelectedClient, sortedLocations]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!tempLocation) {
      return citiesData.data;
    }

    const availableLocations = tempLocation
      ? [tempLocation]
      : filteredLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [tempLocation, citiesData, filteredLocations, tempSelectedClient]);

  useEffect(() => {
    setTempLocation(undefined);
    setTempSelectedCity(undefined);
  }, [tempSelectedClient]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempLocation]);

  useEffect(() => {
    setTempApprovedBy(undefined);
  }, [tempSelectedClient, tempLocation]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <>
      <Grid container spacing={1} mb={3}>
        <Grid item xs={12} md={10}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search"
                value={tempSearchBy}
                onChange={(event) => handleSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                    >
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* clients */}
            <Grid item xs={12} md={2.5}>
              <CustomAutocomplete
                key={tempSelectedClient?.id ?? "client-autocomplete"}
                size="small"
                disablePortal
                id="combo-box-demo"
                options={sortedClients}
                getOptionLabel={(option) => option.clientInfo.nameEn}
                getOptionKey={(option) => `${option.id}`}
                value={tempSelectedClient}
                onChange={handleChangeClient}
                renderInput={(params: any) => (
                  <TextField fullWidth {...params} label={"Client"} />
                )}
              />
            </Grid>

            {/* locations */}
            <Grid item xs={12} md={2.5}>
              <CustomAutocomplete
                size="small"
                fullWidth
                disablePortal
                id="combo-box-demo"
                options={filteredLocations}
                key={tempLocation?.id ?? "loc-autocomplete"}
                getOptionLabel={(option: any) => option.nameEn}
                getOptionKey={(option: any) => option.id}
                value={tempLocation}
                onChange={handleChangeLocation}
                renderInput={(params: any) => (
                  <TextField fullWidth {...params} label={"Location"} />
                )}
              />
            </Grid>
            {/* city */}
            <Grid item xs={12} md={2.5}>
              <CustomAutocomplete
                key={tempSelectedCity?.id ?? "city-autocomplete"}
                size="small"
                disablePortal
                id="combo-box-demo"
                options={filteredCities}
                getOptionLabel={(option) => option.nameEn}
                getOptionKey={(option) => `${option.id}`}
                value={tempSelectedCity}
                onChange={handleChangeCity}
                renderInput={(params: any) => (
                  <TextField fullWidth {...params} label={"City"} />
                )}
              />
            </Grid>
            {/* approved by */}
            <Grid item xs={12} md={2.5}>
              <CustomAutocomplete
                key={tempApprovedBy?.id ?? "approved-by-autocomplete"}
                size="small"
                disablePortal
                id="combo-box-demo"
                options={approvalEmployeesData?.data ?? []}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={tempApprovedBy}
                onChange={handleChangeApprovedBy}
                renderInput={(params: any) => (
                  <TextField fullWidth {...params} label={"Approved By"} />
                )}
                loading={isFetchingApprovals}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              {/* start date */}
              <DatePicker
                label="Start Date"
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setOpenStartDate(true),
                    inputProps: { readOnly: true },
                    fullWidth: true,
                  },
                }}
                format={"dd/MM/yyyy"}
                value={tempStartDate}
                onChange={(newValue) => setTempStartDate(newValue)}
                monthsPerRow={4}
                name="start date"
                disableOpenPicker
                open={openStartDate}
                onClose={() => setOpenStartDate(false)}
                minDate={minDate}
                maxDate={maxDate}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              {/* end date */}
              <DatePicker
                label="End Date"
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setOpenEndDate(true),
                    inputProps: { readOnly: true },
                    fullWidth: true,
                  },
                }}
                format={"dd/MM/yyyy"}
                value={tempEndDate}
                onChange={(newValue) => setTempEndDate(newValue)}
                name="end date"
                disableOpenPicker
                open={openEndDate}
                onClose={() => setOpenEndDate(false)}
                minDate={minDate}
                maxDate={maxDate}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          md={2}
          display={"flex"}
          justifyContent={"flex-end"}
          direction={"row"}
          gap={1}
        >
          <Button onClick={handleResetFilers}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </Button>
        </Grid>
      </Grid>

      {!selectedClient && !location && !searchBy ? (
        <NoFilterSelected />
      ) : (
        <>
          <Box mb={3}>
            {!emptyDataArr ? (
              <Grid
                item
                xs={12}
                md={2}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <LoadingButton
                  variant="contained"
                  sx={{ gap: 0.5 }}
                  onClick={handleExport}
                  loading={isFetchingExport}
                >
                  Export
                </LoadingButton>
              </Grid>
            ) : null}
          </Box>
          <Paper
            sx={{
              bgcolor: "#1A1A1A",
              boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
              padding: { xs: "15px", md: "30px" },
            }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    {tableHeadText.map((item) => (
                      <TableCell
                        key={item}
                        sx={{
                          fontWeight: 600,
                          color: "#A1A5B7",
                        }}
                      >
                        {item}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableLoadingSkeleton
                    rowsLength={rowsPerPage}
                    cellsLength={13}
                  />
                ) : (
                  <TableBody>
                    {transactionsData?.data?.map((row: any) => {
                      const { paymentStatus } = row;
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                          }}
                          onClick={() => {
                            setOpenValidationDetails(true);
                            setClickedTrans(row);
                          }}
                        >
                          <TableCell>{row.ticketNumber}</TableCell>
                          <TableCell>{row.location}</TableCell>
                          <TableCell>{row.date}</TableCell>
                          <TableCell>{row?.checkInTime}</TableCell>
                          <TableCell>{row.requestedTime}</TableCell>
                          <TableCell>{row.validatedAt}</TableCell>
                          <TableCell>{row.checkOutTime}</TableCell>
                          <TableCell>{row.totalHours}</TableCell>
                          <TableCell>{row.validatedHours}</TableCell>
                          <TableCell>{row.amount}</TableCell>
                          <TableCell>{row.amountAfterValidation}</TableCell>
                          <TableCell>{row.approvedBy}</TableCell>
                          <TableCell>{row.notes}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </Table>

              {emptyDataArr && <NoData />}

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                disabled={isLoading}
              />
            </TableContainer>
            {openValidationDetails && !!clickedTrans ? (
              <TransactionsDetailsDrawer
                transDetails={clickedTrans?.ticketDetails}
                open={openValidationDetails}
                onClose={handleCloseDrawer}
              />
            ) : null}
          </Paper>
        </>
      )}
    </>
  );
};
export default TransactionsTable;
